import React, { useRef, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  HStack,
  Badge,
  Spinner,
  IconButton,
  Image,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Buildings,
  MicrophoneStage,
  MapPin,
  CalendarBlank,
  CurrencyDollar,
  Camera,
  Trash,
  DotsThree,
  CheckCircle,
  Archive,
  ArrowCounterClockwise,
} from 'phosphor-react';
import moment from 'moment';
import 'moment/locale/pt-br';
import { toast } from 'sonner';
import { useEventos, IEventoDetalhe as IEventoDetalheType } from '../../../hooks/useEventos';
import DialogModal from '../../../components/DialogModal';
import { IDataNoPagination } from '../../../types/shared';
import { formatCurrency } from '../../../utils/FormatFunctions';

const STATUS_EVENTO: Record<string, { color: string; label: string }> = {
  pago: { color: 'green', label: 'Pago' },
  nf_emitida: { color: 'purple', label: 'NF Emitida' },
  aguardando_nf: { color: 'teal', label: 'Aguardando NF' },
  assinado: { color: 'cyan', label: 'Assinado' },
  enviado_assinatura: { color: 'orange', label: 'Enviado p/ Ass.' },
  contrato: { color: 'blue', label: 'Contrato' },
  proposta: { color: 'yellow', label: 'Proposta' },
};

/**
 * Cabecalho compacto da pagina do evento: voltar, logo, dados e o menu de finalizar, arquivar e reabrir. Os numeros
 * ficam na aba Gerencial, para o Kanban ganhar a altura da tela.
 */
export default function EventoDetalhe({
  eventoId,
  onVoltar,
}: {
  eventoId: number;
  onVoltar: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { getEvento, uploadLogo, deleteLogo, updateEvento } = useEventos();

  const { data: eventoData, isLoading } = useQuery<IDataNoPagination<IEventoDetalheType>>({
    queryKey: ['evento-detalhe', eventoId],
    queryFn: getEvento,
    enabled: !!eventoId,
  });

  const evento = eventoData?.data;

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => uploadLogo({ eventoId, file }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evento-detalhe', eventoId] });
      queryClient.invalidateQueries({ queryKey: ['eventos'] });
      toast.success('Logo enviada!');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Erro ao enviar logo');
    },
  });

  const deleteLogoMutation = useMutation({
    mutationFn: () => deleteLogo(eventoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evento-detalhe', eventoId] });
      queryClient.invalidateQueries({ queryKey: ['eventos'] });
      toast.success('Logo removida!');
    },
    onError: () => {
      toast.error('Erro ao remover logo');
    },
  });

  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const statusMutation = useMutation({
    mutationFn: (novoStatus: string) =>
      updateEvento({ id: eventoId, data: { status: novoStatus } }),
    onSuccess: (_data, novoStatus) => {
      queryClient.invalidateQueries({ queryKey: ['evento-detalhe', eventoId] });
      queryClient.invalidateQueries({ queryKey: ['eventos'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-projects'] });
      const msgs: Record<string, string> = {
        concluido: 'Evento finalizado!',
        cancelado: 'Evento arquivado!',
        ativo: 'Evento reaberto!',
      };
      toast.success(msgs[novoStatus] || 'Status atualizado!');
    },
    onError: () => toast.error('Erro ao atualizar status'),
  });

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.warning('Formato inválido. Use JPG, PNG ou WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.warning('Imagem muito grande. Máximo 5MB.');
      return;
    }
    uploadLogoMutation.mutate(file);
    e.target.value = '';
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="lg" color="gray.300" thickness="3px" />
      </Flex>
    );
  }

  if (!evento) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Text color="gray.400" fontSize="14px">
          Evento não encontrado
        </Text>
      </Flex>
    );
  }

  return (
    <Box>
      {/* Header Card */}
      <Box
        bg="white"
        borderRadius="16px"
        border="1px solid"
        borderColor="gray.100"
        overflow="hidden"
        mb={4}
        boxShadow="0 1px 2px rgba(0,0,0,0.04)"
      >
        <Box h="3px" bgGradient="linear(to-r, #6366F1, #8B5CF6)" />
        <Box px={4} py={3}>
          <Flex justify="space-between" align="center" flexWrap="wrap" rowGap={2}>
            <HStack spacing={3} flex="1 1 280px" minW={0} align="center">
              <IconButton
                aria-label="Voltar para Eventos"
                icon={<ArrowLeft size={18} weight="bold" />}
                variant="ghost"
                size="sm"
                borderRadius="full"
                color="gray.400"
                _hover={{ bg: 'gray.100', color: 'gray.600' }}
                onClick={onVoltar}
              />
              {/* Logo do evento */}
              <Box position="relative" flexShrink={0}>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleLogoUpload}
                  display="none"
                />
                {evento.logo_url ? (
                  <Box
                    w="48px"
                    h="48px"
                    borderRadius="12px"
                    overflow="hidden"
                    border="1px solid"
                    borderColor="gray.200"
                    position="relative"
                    cursor="pointer"
                    role="group"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Image
                      src={evento.logo_url}
                      alt="Logo do evento"
                      w="100%"
                      h="100%"
                      objectFit="cover"
                    />
                    <Flex
                      position="absolute"
                      inset={0}
                      bg="blackAlpha.600"
                      align="center"
                      justify="center"
                      opacity={0}
                      _groupHover={{ opacity: 1 }}
                      transition="opacity 0.15s ease"
                      borderRadius="12px"
                    >
                      <Camera size={20} color="white" weight="bold" />
                    </Flex>
                    <IconButton
                      aria-label="Remover logo"
                      icon={<Trash size={12} weight="bold" />}
                      size="xs"
                      colorScheme="red"
                      borderRadius="full"
                      position="absolute"
                      top={-1}
                      right={-1}
                      minW="20px"
                      h="20px"
                      opacity={0}
                      _groupHover={{ opacity: 1 }}
                      transition="opacity 0.15s ease"
                      isLoading={deleteLogoMutation.isPending}
                      onClick={e => {
                        e.stopPropagation();
                        deleteLogoMutation.mutate();
                      }}
                    />
                  </Box>
                ) : (
                  <Flex
                    w="48px"
                    h="48px"
                    borderRadius="12px"
                    border="2px dashed"
                    borderColor="gray.200"
                    align="center"
                    justify="center"
                    direction="column"
                    cursor="pointer"
                    bg="gray.50"
                    _hover={{ borderColor: 'gray.300', bg: 'gray.100' }}
                    transition="all 0.15s ease"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploadLogoMutation.isPending ? (
                      <Spinner size="sm" color="gray.300" />
                    ) : (
                      <>
                        <Camera size={18} color="#A0AEC0" />
                        <Text fontSize="8px" color="gray.400" mt={1} fontWeight="500">
                          Logo
                        </Text>
                      </>
                    )}
                  </Flex>
                )}
              </Box>
              <Box flex={1}>
                <Text fontWeight="700" fontSize="18px" color="gray.800" letterSpacing="-0.02em">
                  {evento.nome}
                </Text>
                <HStack spacing={3} mt={1} flexWrap="wrap">
                  {/* empresa, artista e "Sobre" vinham no cabeçalho da página antiga do Kanban */}
                  {evento.empresa_nome && (
                    <HStack spacing={1.5} color="gray.400" fontSize="12px">
                      <Buildings size={13} />
                      <Text>{evento.empresa_nome}</Text>
                    </HStack>
                  )}
                  {evento.artista && (
                    <HStack spacing={1.5} color="gray.400" fontSize="12px">
                      <MicrophoneStage size={13} />
                      <Text>{evento.artista}</Text>
                    </HStack>
                  )}
                  {evento.dt_evento && (
                    <HStack spacing={1.5} color="gray.400" fontSize="12px">
                      <CalendarBlank size={13} />
                      <Text>{moment(evento.dt_evento).format('DD [de] MMMM [de] YYYY')}</Text>
                    </HStack>
                  )}
                  {(evento.local || evento.cidade) && (
                    <HStack spacing={1.5} color="gray.400" fontSize="12px">
                      <MapPin size={13} />
                      <Text>
                        {evento.local}
                        {evento.cidade ? ` · ${evento.cidade}` : ''}
                        {evento.uf ? ` ${evento.uf}` : ''}
                      </Text>
                    </HStack>
                  )}
                  <HStack spacing={1.5} fontSize="12px">
                    <CurrencyDollar size={13} color="#F59E0B" />
                    <Text fontWeight="600" color="gray.600">
                      {formatCurrency(evento.valor_orcamento)}
                    </Text>
                  </HStack>
                </HStack>
                {evento.descricao && (
                  <Text fontSize="12px" color="gray.400" mt={1.5} maxW="640px" noOfLines={2}>
                    <Text as="span" fontWeight="600" color="gray.500">
                      Sobre:{' '}
                    </Text>
                    {evento.descricao}
                  </Text>
                )}
              </Box>
            </HStack>
            <HStack spacing={2} ml="auto">
              {evento.ultimo_status_contrato && STATUS_EVENTO[evento.ultimo_status_contrato] && (
                <Badge
                  colorScheme={STATUS_EVENTO[evento.ultimo_status_contrato].color}
                  fontSize="11px"
                  fontWeight="600"
                  borderRadius="md"
                  px={2.5}
                  py={1}
                >
                  + {STATUS_EVENTO[evento.ultimo_status_contrato].label}
                </Badge>
              )}
              {['ativo', 'concluido', 'cancelado'].includes(evento.status) && (
                <Menu>
                  <MenuButton
                    as={IconButton}
                    icon={<DotsThree size={18} weight="bold" />}
                    variant="ghost"
                    size="sm"
                    borderRadius="full"
                    color="gray.400"
                    aria-label="Acoes do evento"
                    isDisabled={statusMutation.isPending}
                    _hover={{ bg: 'gray.100', color: 'gray.600' }}
                  />
                  <Portal>
                    <MenuList
                      borderRadius="12px"
                      boxShadow="lg"
                      border="1px solid"
                      borderColor="gray.100"
                      py={1}
                      minW="180px"
                    >
                      {evento.status === 'ativo' && (
                        <>
                          <MenuItem
                            icon={<CheckCircle size={14} />}
                            fontSize="13px"
                            py={2}
                            borderRadius="lg"
                            mx={1}
                            color="green.600"
                            _hover={{ bg: 'green.50' }}
                            onClick={() => setPendingAction('concluido')}
                          >
                            Finalizar evento
                          </MenuItem>
                          <MenuItem
                            icon={<Archive size={14} />}
                            fontSize="13px"
                            py={2}
                            borderRadius="lg"
                            mx={1}
                            color="gray.600"
                            _hover={{ bg: 'gray.50' }}
                            onClick={() => setPendingAction('cancelado')}
                          >
                            Arquivar evento
                          </MenuItem>
                        </>
                      )}
                      {(evento.status === 'concluido' || evento.status === 'cancelado') && (
                        <MenuItem
                          icon={<ArrowCounterClockwise size={14} />}
                          fontSize="13px"
                          py={2}
                          borderRadius="lg"
                          mx={1}
                          color="blue.600"
                          _hover={{ bg: 'blue.50' }}
                          onClick={() => setPendingAction('ativo')}
                        >
                          Reabrir evento
                        </MenuItem>
                      )}
                    </MenuList>
                  </Portal>
                </Menu>
              )}
            </HStack>
          </Flex>
        </Box>
      </Box>

      {(() => {
        const dialogConfig: Record<
          string,
          { title: string; description: string; status: 'info' | 'warning' | 'success' | 'error' }
        > = {
          concluido: {
            title: 'Finalizar evento',
            description: 'O projeto Kanban deste evento será arquivado. Deseja continuar?',
            status: 'info',
          },
          cancelado: {
            title: 'Arquivar evento',
            description: 'O evento e o projeto Kanban serão arquivados. Deseja continuar?',
            status: 'warning',
          },
          ativo: {
            title: 'Reabrir evento',
            description: 'O evento será reaberto e o projeto Kanban será restaurado.',
            status: 'info',
          },
        };
        const cfg = pendingAction ? dialogConfig[pendingAction] : null;
        return (
          <DialogModal
            isOpen={pendingAction !== null}
            status={cfg?.status || 'info'}
            title={cfg?.title || ''}
            description={cfg?.description || ''}
            onConfirm={() => {
              if (pendingAction) statusMutation.mutate(pendingAction);
              setPendingAction(null);
            }}
            onCancel={() => setPendingAction(null)}
            confirmLabel="Confirmar"
            cancelLabel="Cancelar"
          />
        );
      })()}
    </Box>
  );
}
